/**
 * Le nom d'édition d'une festivité : le nom, plus l'année de sa date.
 *
 * L'année ne fait pas partie du nom enregistré — « Fête votive », pas « Fête
 * votive 2026 » : c'est ce qui permet de reconnaître les éditions d'une même
 * fête d'une année sur l'autre, même quand une édition a sauté (intempéries).
 * L'édition se lit sur la date de programmation, et c'est l'affichage qui
 * recompose « Fête votive 2026 ».
 *
 * Un nom qui porte déjà un millésime — les anciennes fiches, les données
 * d'essai — est laissé tel quel : « Fête votive 2025 2025 » serait pire que
 * l'ancien usage.
 */
export function nomEdition(nom: string, dateDebut?: string | null): string {
  if (!dateDebut || /\b(19|20)\d{2}\b/.test(nom)) {
    return nom;
  }

  const annee = new Date(dateDebut).getFullYear();

  return Number.isFinite(annee) ? `${nom} ${annee}` : nom;
}

/**
 * Le nom sans millésime : la clé qui rapproche les éditions, et la valeur
 * qu'on propose à la création d'une nouvelle édition.
 */
export function nomSansMillesime(nom: string): string {
  return nom
    .replace(/\b(19|20)\d{2}\b/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
/**
 * La période d'une festivité, dite comme on la dirait à voix haute.
 *
 * L'affichage montrait la date de DÉBUT, puis deux heures — « jeudi 13 août,
 * 18:00 – 23:59 ». Pour une fête votive qui court du 13 au 15, cela se lit
 * comme une soirée qui finit avant minuit : la date de fin disparaissait
 * purement et simplement, et l'on venait le mauvais jour.
 *
 * Trois formes, selon ce que la période est réellement :
 *
 *   même jour        jeudi 13 août 2026, de 18:00 à 23:00
 *   même mois        du jeudi 13 au samedi 15 août 2026
 *   à cheval         du jeudi 30 juillet au samedi 1 août 2026
 *
 * L'année n'est écrite qu'une fois, à la fin : « du jeudi 13 août 2026 au
 * samedi 15 août 2026 » se lit deux fois plus lentement pour la même
 * information.
 *
 * Les heures ne sont reprises sur une période de plusieurs jours que
 * lorsqu'elles disent quelque chose : une fête « de 18:00 à 23:59 » sur trois
 * jours ne commence pas à 18 h chaque soir, et l'écrire tromperait. On les
 * garde donc pour le seul cas du jour unique, où elles sont exactes.
 */

const JOUR_MOIS_ANNEE = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

const JOUR_MOIS = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

const JOUR_SEUL = new Intl.DateTimeFormat('fr-FR', {
  weekday: 'long',
  day: 'numeric',
});

const HEURE = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });

/** Deux instants tombent-ils le même jour civil ? */
function memeJour(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function periodeEvenement(dateDebut: string, dateFin?: string | null): string {
  const debut = new Date(dateDebut);
  if (Number.isNaN(debut.getTime())) return '';

  const fin = dateFin ? new Date(dateFin) : null;
  const finValide = fin && !Number.isNaN(fin.getTime()) ? fin : null;

  // Pas de fin, ou fin le même jour : un jour, et ses heures.
  if (!finValide || memeJour(debut, finValide)) {
    const jour = JOUR_MOIS_ANNEE.format(debut);
    if (!finValide) return jour;

    return `${jour}, de ${HEURE.format(debut)} à ${HEURE.format(finValide)}`;
  }

  // Plusieurs jours : on ne répète que ce qui change.
  const memeAnnee = debut.getFullYear() === finValide.getFullYear();
  const memeMois = memeAnnee && debut.getMonth() === finValide.getMonth();

  if (memeMois) {
    return `du ${JOUR_SEUL.format(debut)} au ${JOUR_MOIS_ANNEE.format(finValide)}`;
  }

  // Une soirée de réveillon court sur deux années : les écrire toutes les
  // deux, sans quoi « du jeudi 31 décembre au vendredi 1 janvier 2027 » laisse
  // deviner que le 31 était en 2026 — ce qui est vrai, mais se devine.
  return memeAnnee
    ? `du ${JOUR_MOIS.format(debut)} au ${JOUR_MOIS_ANNEE.format(finValide)}`
    : `du ${JOUR_MOIS_ANNEE.format(debut)} au ${JOUR_MOIS_ANNEE.format(finValide)}`;
}

/**
 * Une festivité tient-elle sur plus d'un jour ?
 *
 * L'écran s'en sert pour décider s'il doit signaler les créneaux du repas et du
 * concert : sur un seul jour, la date de l'événement suffit et la répéter
 * encombre ; sur trois, c'est précisément ce qu'il faut dire.
 */
export function surPlusieursJours(dateDebut: string, dateFin?: string | null): boolean {
  if (!dateFin) return false;
  const debut = new Date(dateDebut);
  const fin = new Date(dateFin);
  if (Number.isNaN(debut.getTime()) || Number.isNaN(fin.getTime())) return false;

  return !memeJour(debut, fin);
}

/**
 * Le créneau d'une prestation — repas, concert — au sein de la festivité.
 *
 * Rendu court : « samedi 15 à 20:00 ». Le mois et l'année sont déjà dans la
 * période au-dessus, et les répéter à chaque ligne noierait ce qui distingue
 * ces créneaux les uns des autres, qui est le jour et l'heure.
 */
export function creneauPrestation(date?: string | null): string | null {
  if (!date) return null;
  const quand = new Date(date);
  if (Number.isNaN(quand.getTime())) return null;

  return `${JOUR_SEUL.format(quand)} à ${HEURE.format(quand)}`;
}
